import React, { useState, useEffect } from 'react';
import { XMarkIcon, EnvelopeIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline';
import Helper from '../helpers';
import config from '../config';

const apiBaseUrl = config.apiBaseUrl;

const ContactSupportModal = ({ isOpen, onClose, userType = 'Dashboard User' }) => {
    const [senderName, setSenderName] = useState('');
    const [senderEmail, setSenderEmail] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            // Pre-fill email from stored cookie
            const prefillEmail = async () => {
                try {
                    const email = await Helper.getCookie('wh.user.email');
                    if (email) setSenderEmail(email);
                } catch {
                    // ignore
                }
            };
            prefillEmail();
            // Reset state when modal opens
            setSuccess(false);
            setError('');
            setMessage('');
        }
    }, [isOpen]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!senderEmail.trim()) {
            setError('Please provide your email address.');
            return;
        }
        if (!message.trim()) {
            setError('Please enter a message.');
            return;
        }

        setLoading(true);
        try {
            const token = await Helper.getCookie('wh-jwt');
            const response = await fetch(`${apiBaseUrl}/api/support/contact`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    senderName: senderName.trim() || null,
                    senderEmail: senderEmail.trim(),
                    message: message.trim(),
                    userType
                })
            });

            if (response.ok) {
                setSuccess(true);
            } else {
                const data = await response.json().catch(() => ({}));
                setError(data.message || 'Failed to send your request. Please try again.');
            }
        } catch {
            setError('Something went wrong. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setSuccess(false);
        setError('');
        setMessage('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative w-full max-w-lg bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-700">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-emerald-600/20 rounded-lg flex items-center justify-center">
                            <EnvelopeIcon className="h-5 w-5 text-emerald-400" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-white">Contact Support</h2>
                            <p className="text-gray-400 text-xs mt-0.5">We'll get back to you as soon as possible</p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 rounded-lg hover:bg-gray-700 text-gray-400 hover:text-white transition-colors"
                        aria-label="Close"
                    >
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-6">
                    {success ? (
                        <div className="text-center py-6">
                            <CheckCircleIcon className="h-14 w-14 text-emerald-400 mx-auto mb-4" />
                            <h3 className="text-white text-lg font-semibold mb-2">Message Sent!</h3>
                            <p className="text-gray-400 text-sm mb-6">
                                Your support request has been received. We'll be in touch shortly.
                            </p>
                            <button
                                onClick={handleClose}
                                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {error && (
                                <div className="flex items-start gap-2 bg-red-900/20 border border-red-800/40 text-red-300 px-4 py-3 rounded-lg text-sm">
                                    <ExclamationCircleIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                                    {error}
                                </div>
                            )}

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1.5">
                                    Your Name
                                </label>
                                <input
                                    type="text"
                                    value={senderName}
                                    onChange={e => setSenderName(e.target.value)}
                                    placeholder="Optional"
                                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-600 text-white placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm transition-colors"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1.5">
                                    Your Email <span className="text-red-400">*</span>
                                </label>
                                <input
                                    type="email"
                                    value={senderEmail}
                                    onChange={e => setSenderEmail(e.target.value)}
                                    placeholder="your@email.com"
                                    required
                                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-600 text-white placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm transition-colors"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1.5">
                                    How can we help? <span className="text-red-400">*</span>
                                </label>
                                <textarea
                                    value={message}
                                    onChange={e => setMessage(e.target.value)}
                                    placeholder="Describe your issue or question..."
                                    required
                                    rows={5}
                                    className="w-full px-4 py-2.5 bg-gray-800 border border-gray-600 text-white placeholder-gray-500 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none text-sm transition-colors resize-none"
                                />
                            </div>

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={handleClose}
                                    className="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors text-sm"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-lg font-medium transition-colors text-sm flex items-center justify-center gap-2"
                                >
                                    {loading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                            Sending...
                                        </>
                                    ) : (
                                        <>
                                            <EnvelopeIcon className="h-4 w-4" />
                                            Send Message
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ContactSupportModal;
